import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Col, Row } from 'reactstrap';
import Select from 'react-select';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import { setFilter } from '../../../redux/actions/general';
import { URL_PARAMS } from '../../../urlSync/urlParamNames';
import {
   filterSelector,
   streamNamesArraySelector,
   streamNamesSelector,
   cancelTokenSelector,
   hashSelector,
} from '../../../redux/selectors/selectors';
import { sortAndReduceFilterArrayToString, filterChanged } from '../../../redux/selectors/comparing';
import {
   WidgetBodyContainer,
   WidgetContainer,
   WidgetHeaderContainer,
} from '../../../styledComponents/styledComponents';
import { DefaultGrey } from '../../../constants/colors';
import { getHttpHeader } from '../../../util/FetchTools';
import { getUnifiedErrorObject } from '../../../util/ErrorUnifier';

const STREAM_MIN_LEN = 11;
const DEBOUNCE_MS = 500;

const VALIDATION_STATUS = {
   IDLE: 'idle',
   CHECKING: 'checking',
   VALID: 'valid',
   INVALID: 'invalid',
   ERROR: 'error',
};

function getValidationColor(status) {
   if (status === VALIDATION_STATUS.VALID) return '#27ae60';
   if (status === VALIDATION_STATUS.INVALID || status === VALIDATION_STATUS.ERROR) return '#e55353';
   return '#73818f';
}

function StreamNameFilter() {
   const dispatch = useDispatch();

   const storeStreamNames = useSelector(streamNamesArraySelector);
   const storeStreamNamesSortedString = useSelector(streamNamesSelector);
   const filter = useSelector(filterSelector);
   const cancelToken = useSelector(cancelTokenSelector);
   const orgaHash = useSelector(hashSelector);

   const [streamNames, setStreamNames] = useState(storeStreamNames);
   const [inputValue, setInputValue] = useState('');
   const [streamNameToCheck, setStreamNameToCheck] = useState('');
   const [validation, setValidation] = useState({ status: VALIDATION_STATUS.IDLE, message: '' });
   const debounceRef = useRef(null);
   const pastedRef = useRef(false);

   const streamNamesRef = useRef();
   streamNamesRef.current = streamNames;

   const header = useMemo(() => getHttpHeader(orgaHash), [orgaHash]);

   // Sync with URL changes
   useEffect(() => {
      if (storeStreamNamesSortedString !== sortAndReduceFilterArrayToString(streamNamesRef.current, '~')) {
         setStreamNames(storeStreamNames);
      }
   }, [storeStreamNames, storeStreamNamesSortedString]);

   // Auto-dispatch when all pills are removed
   useEffect(() => {
      if (streamNames.length === 0 && storeStreamNames.length > 0) {
         dispatch(setFilter({ ...filter, streamNames: [] }, { [URL_PARAMS.STREAM_NAMES]: [] }));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [streamNames.length]);

   // Auto-add after paste + successful validation
   useEffect(() => {
      if (validation.status === VALIDATION_STATUS.VALID && pastedRef.current) {
         pastedRef.current = false;
         handleAdd();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [validation.status]);

   // Debounced validation trigger
   useEffect(() => {
      clearTimeout(debounceRef.current);
      const trimmed = inputValue.trim();

      if (trimmed.length < STREAM_MIN_LEN) {
         setValidation({ status: VALIDATION_STATUS.IDLE, message: '' });
         setStreamNameToCheck('');
         return;
      }

      setValidation({ status: VALIDATION_STATUS.CHECKING, message: '' });
      debounceRef.current = setTimeout(() => {
         setStreamNameToCheck(trimmed);
      }, DEBOUNCE_MS);

      return () => clearTimeout(debounceRef.current);
   }, [inputValue]);

   useQuery({
      enabled: streamNameToCheck !== '',
      queryKey: ['streamNameFilterValidation', streamNameToCheck, orgaHash],
      queryFn: () =>
         axios
            .post(
               '/v2/filters/validate/stream',
               { stream: streamNameToCheck },
               { headers: header, cancelToken: cancelToken?.token },
            )
            .then((res) => res.data),
      onSuccess: (res) => {
         if (res.valid === true) {
            setValidation({ status: VALIDATION_STATUS.VALID, message: '' });
         } else {
            setValidation({
               status: VALIDATION_STATUS.INVALID,
               message: res.info ?? 'Stream not found in selected timespan',
            });
         }
      },
      onError: (err) => {
         if (!axios.isCancel(err)) {
            const error = getUnifiedErrorObject(err);
            setValidation({
               status: VALIDATION_STATUS.ERROR,
               message: error.message ?? 'Validation error',
            });
         }
      },
      retry: false,
      refetchOnWindowFocus: false,
   });

   function handleAdd() {
      const trimmed = inputValue.trim();
      if (!trimmed || validation.status !== VALIDATION_STATUS.VALID) return;
      if (streamNames.some((s) => s.value === trimmed)) return;

      setStreamNames([...streamNames, { value: trimmed, label: trimmed }]);
      setInputValue('');
      setStreamNameToCheck('');
      setValidation({ status: VALIDATION_STATUS.IDLE, message: '' });
   }

   function applySelection() {
      if (streamNames.length === 0 && storeStreamNames.length === 0) return;
      dispatch(setFilter({ ...filter, streamNames }, { [URL_PARAMS.STREAM_NAMES]: streamNames }));
   }

   function getValidationText() {
      const trimmed = inputValue.trim();
      if (validation.status === VALIDATION_STATUS.CHECKING) return '… validating';
      if (validation.status === VALIDATION_STATUS.VALID) return '✓ Valid — press Enter to add';
      if (validation.status === VALIDATION_STATUS.INVALID || validation.status === VALIDATION_STATUS.ERROR)
         return `✕ ${validation.message}`;
      if (trimmed.length > 0 && trimmed.length < STREAM_MIN_LEN)
         return `${STREAM_MIN_LEN - trimmed.length} more character(s) to start validation`;
      return '';
   }

   const validationText = getValidationText();

   return (
      <div style={{ position: 'relative', zIndex: 11 }} className='animated fadeIn'>
         <Row>
            <Col>
               <WidgetContainer>
                  <WidgetHeaderContainer $bgColor={DefaultGrey(1, 247)}>
                     <strong>Stream name filter</strong>
                  </WidgetHeaderContainer>
                  <WidgetBodyContainer>
                     <div className='customRow'>
                        <div className='selectCol'>
                           <div
                              onPaste={() => {
                                 pastedRef.current = true;
                              }}
                           >
                              <Select
                                 components={{ DropdownIndicator: null }}
                                 value={streamNames}
                                 inputValue={inputValue}
                                 onInputChange={(val, { action }) => {
                                    if (action === 'input-change') setInputValue(val);
                                 }}
                                 onChange={(selected) => {
                                    setStreamNames(selected === null ? [] : selected);
                                 }}
                                 onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                       e.preventDefault();
                                       handleAdd();
                                    }
                                 }}
                                 menuIsOpen={false}
                                 isClearable
                                 isMulti
                                 placeholder='Enter stream name to validate, then press Enter to add'
                              />
                           </div>
                           {validationText && (
                              <div
                                 style={{
                                    minHeight: 16,
                                    marginTop: 4,
                                    fontSize: 11,
                                    color: getValidationColor(validation.status),
                                 }}
                              >
                                 {validationText}
                              </div>
                           )}
                        </div>
                        <div className='buttonCol'>
                           <Button
                              color='primary'
                              outline={!filterChanged(streamNames, storeStreamNames)}
                              disabled={!filterChanged(streamNames, storeStreamNames)}
                              style={
                                 !filterChanged(streamNames, storeStreamNames)
                                    ? {
                                         borderColor: 'rgb(230, 230, 230)',
                                         color: '#73818f',
                                         opacity: 1,
                                         cursor: 'not-allowed',
                                         pointerEvents: 'auto',
                                      }
                                    : {}
                              }
                              onClick={applySelection}
                           >
                              Go
                           </Button>
                        </div>
                     </div>
                  </WidgetBodyContainer>
               </WidgetContainer>
            </Col>
         </Row>
      </div>
   );
}

export default StreamNameFilter;
